<template>
  <div class="student-schedule-tab">
    <div v-if="initialLoading" class="loading-block">
      <van-loading type="spinner" />
    </div>

    <div v-else-if="scheduleEntries.length" ref="rootRef">
      <div class="schedule-list">
        <div v-for="item in scheduleEntries" :key="item.key" class="schedule-item">
          <div class="schedule-row">
            <div class="schedule-datetime">{{ item.teachingDate }}  {{ item.startTime }}-{{ item.endTime }}</div>
            <div v-if="item.signStatus === 1" class="schedule-actions">
              <button v-if="new Date() > parseDate(item.endDateTime)" type="button" class="schedule-action attendance" @click="handleAttendance(item)">
                <van-icon name="passed" size="16" />
              </button>
              <button type="button" class="schedule-action danger" @click="handleRemove(item)">
                <van-icon name="delete-o" size="16" />
              </button>
              <button type="button" class="schedule-action primary" @click="handleEdit(item)">
                <van-icon name="edit" size="16" />
              </button>
            </div>
            <div :class="['schedule-status', item.signStatus === 1 ? 'upcoming' : 'history']">
              <span class="status-dot"></span>
              {{ getDictLabel(sign_status, item.signStatus) }}
            </div>
          </div>
          <div class="schedule-teacher">上课老师：{{ item.teacherName }}</div>
          <div class="schedule-course">课程：{{ item.courseName }}</div>
        </div>
      </div>
      <div v-if="loading" class="list-tip">加载中...</div>
      <div v-else-if="finished" class="list-tip">没有更多课表了</div>
    </div>

    <div v-else class="empty-block">暂无课表信息</div>
  </div>
</template>

<script setup>
import { nextTick, onBeforeUnmount, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { showConfirmDialog, showSuccessToast, showToast } from 'vant'
import { useDict } from '@/utils/dict'
import { getSchedulePlanListByStudent, removeSchedulePlan } from '@/api/schedule'
import { parseDate } from '@/utils/common'

const props = defineProps({
  studentId: {
    type: [String, Number],
    default: ''
  }
})

const {
  sign_status
} = useDict(
  'sign_status'
)

const router = useRouter()

const PAGE_SIZE = 10
const LOAD_OFFSET = 32

let activeRequestId = 0
let scrollParent = null

function debugLog(message, payload) {
  if (payload === undefined) {
    return
  }
}

function getDictLabel(list, value) {
  if (value === undefined || value === null || value === '') {
    return ''
  }
  const matched = list.find(item => String(item.dictValue) === String(value))
  return matched ? matched.dictLabel : String(value)
}


const loading = ref(false)
const initialLoading = ref(false)
const finished = ref(false)
const pageNum = ref(1)
const rootRef = ref(null)
const scheduleEntries = ref([])

watch(() => props.studentId, async studentId => {
  activeRequestId += 1
  debugLog('studentId changed', {
    studentId,
    requestId: activeRequestId
  })
  resetListState()
  if (!studentId) {
    finished.value = true
    debugLog('skip loading because studentId is empty')
    return
  }
  await loadFirstPage(studentId, activeRequestId)
}, { immediate: true })

onBeforeUnmount(() => {
  unbindScrollParent()
})

async function handleScroll(event) {
  const target = event?.target || scrollParent
  const metrics = getScrollMetrics(target)
  const reachedBottom = hasReachedBottom(target)
  debugLog('scroll event', {
    ...metrics,
    reachedBottom,
    loading: loading.value,
    finished: finished.value
  })
  if (!reachedBottom) {
    return
  }
  await loadNextPage(activeRequestId)
}

async function loadFirstPage(studentId, requestId) {
  initialLoading.value = true
  debugLog('loading first page', {
    studentId,
    requestId
  })
  try {
    const list = await fetchScheduleEntries({
      studentId,
      pageNum: 1
    })

    if (requestId !== activeRequestId) {
      return
    }

    scheduleEntries.value = list
    pageNum.value = 2
    finished.value = list.length < PAGE_SIZE
    debugLog('first page loaded', {
      size: list.length,
      nextPageNum: pageNum.value,
      finished: finished.value
    })
  } catch (error) {
    if (requestId === activeRequestId) {
      finished.value = true
      showToast(error?.message || '加载课表失败')
    }
  } finally {
    if (requestId === activeRequestId) {
      initialLoading.value = false
      await ensureScrollParent()
      await tryLoadMoreIfNeeded()
    }
  }
}

async function loadNextPage(requestId) {
  if (loading.value || finished.value || !props.studentId) {
    debugLog('skip loading next page', {
      loading: loading.value,
      finished: finished.value,
      studentId: props.studentId
    })
    return
  }

  loading.value = true
  debugLog('loading next page', {
    pageNum: pageNum.value,
    requestId
  })
  try {
    const list = await fetchScheduleEntries({
      studentId: props.studentId,
      pageNum: pageNum.value
    })

    if (requestId !== activeRequestId) {
      return
    }

    scheduleEntries.value = [...scheduleEntries.value, ...list]

    if (list.length < PAGE_SIZE) {
      finished.value = true
    } else {
      pageNum.value += 1
    }
    debugLog('next page loaded', {
      size: list.length,
      nextPageNum: pageNum.value,
      totalSize: scheduleEntries.value.length,
      finished: finished.value
    })
    await ensureScrollParent()
    await tryLoadMoreIfNeeded()
  } catch (error) {
    if (requestId === activeRequestId) {
      finished.value = true
      showToast(error?.message || '加载课表失败')
    }
  } finally {
    if (requestId === activeRequestId) {
      loading.value = false
    }
  }
}

async function fetchScheduleEntries({ studentId, pageNum }) {
  debugLog('requesting schedule entries', {
    studentId,
    pageNum,
    pageSize: PAGE_SIZE
  })
  const result = await getSchedulePlanListByStudent({
    pageNum,
    pageSize: PAGE_SIZE,
    studentId
  })

  const list = Array.isArray(result) ? result : []
  debugLog('received schedule entries', {
    pageNum,
    size: list.length,
    sample: list[0] || null
  })
  return list
}

async function reloadCurrentList() {
  activeRequestId += 1
  const requestId = activeRequestId
  resetListState()
  if (!props.studentId) {
    finished.value = true
    return
  }
  await loadFirstPage(props.studentId, requestId)
}

function handleEdit(item) {
  router.push({
    path: '/editSchedulePlan',
    query: {
      schedulePlanId: item.schedulePlanId,
      courseId: item.courseId
    }
  })
}

function handleAttendance(item) {
  router.push({
    path: '/addAttendance',
    query: {
      schedulePlanId: item.schedulePlanId,
      courseId: item.courseId
    }
  })
}

async function handleRemove(item) {
  try {
    await showConfirmDialog({
      message: '确定要删除这条记录吗？'
    })
    await removeSchedulePlan({
      schedulePlanId: item.schedulePlanId,
      scheduleId: item.scheduleId
    })
    showSuccessToast('操作成功')
    await reloadCurrentList()
  } catch (error) {
    if (error === 'cancel' || error?.message === 'cancel') {
      return
    }
    if (error) {
      showToast(error?.message || '删除排课失败')
    }
  }
}

function resetListState() {
  unbindScrollParent()
  loading.value = false
  initialLoading.value = false
  finished.value = false
  pageNum.value = 1
  scheduleEntries.value = []
}

async function ensureScrollParent() {
  await nextTick()
  if (scrollParent || !rootRef.value) {
    if (!rootRef.value) {
      debugLog('scroll parent unavailable because rootRef is empty')
    }
    return
  }
  scrollParent = findScrollParent(rootRef.value)
  if (scrollParent) {
    scrollParent.addEventListener('scroll', handleScroll, { passive: true })
    debugLog('scroll parent bound', {
      className: scrollParent.className,
      tagName: scrollParent.tagName,
      ...getScrollMetrics(scrollParent)
    })
    return
  }
  debugLog('failed to find scroll parent')
}

function unbindScrollParent() {
  if (scrollParent) {
    scrollParent.removeEventListener('scroll', handleScroll)
    debugLog('scroll parent unbound')
    scrollParent = null
  }
}

async function tryLoadMoreIfNeeded() {
  await nextTick()
  if (!scrollParent || loading.value || initialLoading.value || finished.value) {
    debugLog('skip auto load-more check', {
      hasScrollParent: Boolean(scrollParent),
      loading: loading.value,
      initialLoading: initialLoading.value,
      finished: finished.value
    })
    return
  }
  const reachedBottom = hasReachedBottom(scrollParent)
  debugLog('auto load-more check', {
    ...getScrollMetrics(scrollParent),
    reachedBottom
  })
  if (reachedBottom) {
    await loadNextPage(activeRequestId)
  }
}

function findScrollParent(element) {
  let current = element?.parentElement
  while (current) {
    const style = window.getComputedStyle(current)
    const overflowY = style.overflowY
    if ((overflowY === 'auto' || overflowY === 'scroll') && current.scrollHeight >= current.clientHeight) {
      return current
    }
    current = current.parentElement
  }
  return element?.closest('.van-tabs__content') || null
}

function hasReachedBottom(element) {
  if (!element) {
    return false
  }
  return element.scrollTop + element.clientHeight >= element.scrollHeight - LOAD_OFFSET
}

function getScrollMetrics(element) {
  if (!element) {
    return {
      scrollTop: 0,
      clientHeight: 0,
      scrollHeight: 0
    }
  }
  return {
    scrollTop: element.scrollTop,
    clientHeight: element.clientHeight,
    scrollHeight: element.scrollHeight
  }
}

</script>

<style scoped>
.student-schedule-tab {
  padding-bottom: 8px;
}

.loading-block,
.empty-block,
.list-tip {
  min-height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  font-size: 14px;
}

.empty-block {
  border-radius: 14px;
  background: #f8fafc;
  border: 1px dashed #dbe4ee;
}

.list-tip {
  min-height: 50px;
}

.schedule-list {
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.96);
  overflow: hidden;
}

.schedule-item {
  padding: 14px 2px;
  margin: 0 14px;
}

.schedule-item + .schedule-item {
  border-top: 1px solid rgba(226, 232, 240, 0.72);
}

.schedule-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.schedule-datetime {
  min-width: 0;
  flex: 1;
  font-size: 15px;
  font-weight: 600;
  color: #0f172a;
  line-height: 1.5;
}

.schedule-actions {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}

.schedule-action {
  width: 22px;
  height: 22px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  border: none;
  padding: 0;
}

.schedule-action.attendance {
  color: #16a34a;
  background: rgba(220, 252, 231, 0.92);
}

.schedule-action.primary {
  color: #2563eb;
  background: rgba(219, 234, 254, 0.9);
}

.schedule-action.danger {
  color: #dc2626;
  background: rgba(254, 226, 226, 0.92);
}

.schedule-status {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  white-space: nowrap;
  flex-shrink: 0;
}

.schedule-status.upcoming {
  color: #2563eb;
}

.schedule-status.history {
  color: #16a34a;
}

.status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
}

.schedule-teacher {
  margin-top: 6px;
  font-size: 13px;
  color: #0f172a;
  line-height: 1.5;
}

.schedule-course {
  margin-top: 4px;
  font-size: 13px;
  color: #0f172a;
  line-height: 1.5;
}

</style>
